<template>
  <section id="product-view" class="enterprise-section enterprise-features" aria-labelledby="enterprise-features-title">
    <div class="enterprise-shell">
      <div class="enterprise-section-heading">
        <h2 id="enterprise-features-title">{{ t('enterprise_home_features_title') }}</h2>
        <p>{{ t('enterprise_home_features_description') }}</p>
      </div>

      <div class="enterprise-tabs" role="tablist" :aria-label="t('enterprise_home_nav_features')">
        <button
          v-for="(feature, index) in features"
          :id="`enterprise-feature-tab-${feature.key}`"
          :key="feature.key"
          type="button"
          role="tab"
          :aria-controls="`enterprise-feature-panel-${feature.key}`"
          :aria-selected="selectedIndex === index"
          :class="{ active: selectedIndex === index }"
          @click="selectedIndex = index"
        >
          <EnterpriseIcon :src="featureIcons[index]" />
          <span>{{ feature.label }}</span>
        </button>
      </div>

      <div
        :id="`enterprise-feature-panel-${activeFeature.key}`"
        class="enterprise-feature-panel"
        :class="`is-${activeFeature.key}`"
        role="tabpanel"
        :aria-labelledby="`enterprise-feature-tab-${activeFeature.key}`"
      >
        <div class="enterprise-feature-panel__copy">
          <h3>{{ activeFeature.title }}</h3>
          <p>{{ activeFeature.description }}</p>
          <ul class="enterprise-check-list">
            <li v-for="bullet in activeFeature.bullets" :key="bullet">
              <EnterpriseIcon :src="circleCheckIcon" />
              <span>{{ bullet }}</span>
            </li>
          </ul>
          <NuxtLink :to="featureTarget" class="enterprise-inline-link">
            <span>{{ activeFeature.action }}</span>
            <EnterpriseIcon :src="arrowRightIcon" />
          </NuxtLink>
        </div>

        <div class="enterprise-feature-preview" :class="`is-${activeFeature.key}`">
          <template v-if="activeFeature.key === 'document'">
            <div class="enterprise-document-flow">
              <template v-for="(card, index) in documentCards" :key="card.tag">
                <div class="enterprise-document-card">
                  <span class="enterprise-preview-tag" :class="card.translated ? 'is-translated' : 'is-source'">{{ card.tag }}</span>
                  <i class="enterprise-preview-title-line" :style="{ width: `${card.titleWidth}px` }" />
                  <i v-for="width in card.beforeLines" :key="`before-${width}`" class="enterprise-preview-line" :style="{ width: `${width}px` }" />
                  <div class="enterprise-preview-chart-box">
                    <EnterpriseIcon :src="chartColumnIcon" />
                  </div>
                  <i v-for="width in card.afterLines" :key="`after-${width}`" class="enterprise-preview-line" :style="{ width: `${width}px` }" />
                </div>
                <div v-if="index === 0" class="enterprise-preview-arrow-wrap">
                  <span class="enterprise-preview-arrow-bubble">
                    <EnterpriseIcon :src="languagesIcon" />
                  </span>
                </div>
              </template>
            </div>
          </template>

          <template v-else-if="activeFeature.key === 'image'">
            <div class="enterprise-image-flow">
              <div class="enterprise-image-scan-card">
                <span class="enterprise-preview-tag is-source">{{ t('enterprise_home_preview_scanned_zh') }}</span>
                <div class="enterprise-ocr-photo">
                  <div class="enterprise-ocr-photo__icon"><EnterpriseIcon :src="imageIcon" /></div>
                  <i class="enterprise-ocr-zone" />
                  <i class="enterprise-ocr-zone is-104" />
                  <i class="enterprise-ocr-zone is-122" />
                  <i class="enterprise-ocr-zone is-large" />
                </div>
              </div>
              <div class="enterprise-preview-arrow-wrap">
                <span class="enterprise-preview-arrow-bubble">
                  <EnterpriseIcon :src="scanTextIcon" />
                </span>
              </div>
              <div class="enterprise-image-output-card">
                <span class="enterprise-preview-tag is-translated">{{ t('enterprise_home_preview_translation_en') }}</span>
                <i class="enterprise-preview-title-line is-104" />
                <i class="enterprise-preview-line is-142" />
                <i class="enterprise-preview-line is-120" />
                <i class="enterprise-preview-line is-133" />
                <div class="enterprise-image-table" />
                <i class="enterprise-preview-line is-128" />
                <i class="enterprise-preview-line is-96" />
              </div>
            </div>
          </template>

          <template v-else-if="activeFeature.key === 'editing'">
            <div class="enterprise-editor-card">
              <div class="enterprise-editor-card__toolbar">
                <EnterpriseIcon :src="undoIcon" />
                <EnterpriseIcon :src="redoIcon" />
                <EnterpriseIcon :src="messagePlusIcon" />
                <EnterpriseIcon :src="historyIcon" />
              </div>
              <div class="enterprise-editor-card__body">
                <div class="enterprise-editor-column is-source">
                  <strong>{{ t('enterprise_home_preview_source_zh') }}</strong>
                  <i /><i class="is-128" /><i class="is-142" /><i class="is-highlight" /><i class="is-112" />
                </div>
                <div class="enterprise-editor-column is-translated">
                  <strong>{{ t('enterprise_home_preview_translation_en') }}</strong>
                  <i /><i class="is-120" /><i class="is-135" />
                  <div class="enterprise-editor-column__active"><i /><b /></div>
                  <i class="is-104" />
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="activeFeature.key === 'history'">
            <div class="enterprise-permission-card">
              <div class="enterprise-permission-card__head">
                <strong>{{ t('enterprise_home_preview_member_permissions') }}</strong><span>{{ t('enterprise_home_preview_view') }}</span><span>{{ t('enterprise_home_preview_download') }}</span><span>{{ t('enterprise_home_preview_delete') }}</span>
              </div>
              <div v-for="member in permissionRows" :key="member.name" class="enterprise-permission-card__row">
                <strong><EnterpriseIcon :src="userIcon" />{{ member.name }}</strong>
                <span v-for="(allowed, index) in member.permissions" :key="index" :class="{ muted: !allowed }">
                  <EnterpriseIcon :src="allowed ? circleCheckIcon : circleMinusIcon" />
                </span>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="enterprise-glossary-card">
              <div class="enterprise-glossary-card__head">
                <strong>{{ t('enterprise_home_preview_glossary_count') }}</strong>
                <span class="enterprise-glossary-card__upload">
                  <EnterpriseIcon :src="uploadIcon" />
                  {{ t('enterprise_home_preview_glossary_import') }}
                </span>
              </div>
              <div v-for="term in glossaryTerms" :key="term.source" class="enterprise-glossary-card__row">
                <div>
                  <strong>{{ term.source }}</strong>
                  <EnterpriseIcon :src="arrowRightIcon" />
                  <span>{{ term.target }}</span>
                </div>
                <span class="enterprise-lock"><EnterpriseIcon :src="lockIcon" />{{ t('enterprise_home_preview_locked') }}</span>
              </div>
              <div class="enterprise-glossary-card__hit">
                <strong>{{ t('enterprise_home_preview_glossary_hit') }}</strong>
                <p>{{ t('enterprise_home_preview_glossary_sentence_before') }}<mark>VAT special invoice</mark>{{ t('enterprise_home_preview_glossary_sentence_after') }}</p>
              </div>
            </div>
          </template>

          <div class="enterprise-preview-chip" :class="{ 'is-history': activeFeature.key === 'history' }">
            <EnterpriseIcon :src="activePreviewMeta.icon" />
            <span>{{ activePreviewMeta.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { getEnterpriseFeatures } from '~/config/enterpriseLanding'
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import arrowRightIcon from '~/assets/images/enterprise/icons/arrow-right.svg'
import bookMarkedIcon from '~/assets/images/enterprise/icons/book-marked.svg'
import chartColumnIcon from '~/assets/images/enterprise/icons/chart-column.svg'
import circleCheckIcon from '~/assets/images/enterprise/icons/circle-check.svg'
import circleMinusIcon from '~/assets/images/enterprise/icons/circle-minus.svg'
import fileTextIcon from '~/assets/images/enterprise/icons/file-text.svg'
import folderOpenIcon from '~/assets/images/enterprise/icons/folder-open.svg'
import historyIcon from '~/assets/images/enterprise/icons/history.svg'
import imageIcon from '~/assets/images/enterprise/icons/image.svg'
import languagesIcon from '~/assets/images/enterprise/icons/languages.svg'
import layoutTemplateIcon from '~/assets/images/enterprise/icons/layout-template.svg'
import lockIcon from '~/assets/images/enterprise/icons/lock.svg'
import messagePlusIcon from '~/assets/images/enterprise/icons/message-square-plus.svg'
import penLineIcon from '~/assets/images/enterprise/icons/pen-line.svg'
import redoIcon from '~/assets/images/enterprise/icons/redo-2.svg'
import scanTextIcon from '~/assets/images/enterprise/icons/scan-text.svg'
import scrollTextIcon from '~/assets/images/enterprise/icons/scroll-text.svg'
import undoIcon from '~/assets/images/enterprise/icons/undo-2.svg'
import uploadIcon from '~/assets/images/enterprise/icons/upload.svg'
import userIcon from '~/assets/images/enterprise/icons/user.svg'

const localePath = useLocalePath()
const { t } = useI18n()
const features = computed(() => getEnterpriseFeatures(t))
const selectedIndex = ref(0)
const activeFeature = computed(() => features.value[selectedIndex.value])
const featureIcons = [fileTextIcon, imageIcon, penLineIcon, folderOpenIcon, bookMarkedIcon]

const previewMeta = computed(() => ({
  document: { icon: layoutTemplateIcon, label: t('enterprise_home_preview_document_meta') },
  image: { icon: scanTextIcon, label: t('enterprise_home_preview_image_meta') },
  editing: { icon: historyIcon, label: t('enterprise_home_preview_editing_meta') },
  history: { icon: scrollTextIcon, label: t('enterprise_home_preview_history_meta') },
  glossary: { icon: bookMarkedIcon, label: t('enterprise_home_preview_glossary_meta') },
}))
const activePreviewMeta = computed(() => previewMeta.value[activeFeature.value.key])

const documentCards = computed(() => [
  { tag: t('enterprise_home_preview_source_zh'), translated: false, titleWidth: 92, beforeLines: [138, 120, 131], afterLines: [130, 142, 96] },
  { tag: t('enterprise_home_preview_translation_en'), translated: true, titleWidth: 104, beforeLines: [142, 126, 135], afterLines: [130, 142, 96] },
])

const permissionRows = computed(() => [
  { name: t('enterprise_home_preview_member_marketing'), permissions: [true, true, false] },
  { name: t('enterprise_home_preview_member_legal'), permissions: [true, true, true] },
  { name: t('enterprise_home_preview_member_external'), permissions: [true, false, false] },
])

const glossaryTerms = computed(() => [
  { source: 'DOCLINGO', target: 'Doclingo' },
  { source: t('enterprise_home_preview_term_hd'), target: 'HD Redraw' },
  { source: t('enterprise_home_preview_term_vat'), target: 'VAT special invoice' },
])

const featureTarget = computed(() => {
  if (activeFeature.value.key === 'image') return localePath('/account?menu=image')
  if (activeFeature.value.key === 'history') return localePath('/account?menu=records')
  if (activeFeature.value.key === 'glossary') return localePath('/account?menu=glossary')
  if (activeFeature.value.key === 'editing') return localePath('/account?menu=home')
  return localePath('/account?menu=home')
})
</script>
